import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Sparkles, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { heroContent } from '../data/mock';

const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollProgress = Math.max(0, -rect.top);
        setScrollY(scrollProgress);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="home" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white"
    >
      {/* Background Image with Subtle Parallax */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0"
          style={{
            transform: `translateY(${scrollY * 0.3}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        >
          <img 
            src={heroContent.heroImage}
            alt="Industrial Welding" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/70"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white"></div>
      </div>

      {/* Subtle Geometric Pattern */}
      <div className="absolute inset-0 z-0 opacity-5">
        <div 
          className="absolute inset-0" 
          style={{
            backgroundImage: `linear-gradient(rgba(234, 179, 8, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(234, 179, 8, 0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        ></div>
      </div>

      {/* Made in India Badge - Top Right */}
      <div className="absolute top-24 right-8 z-[60] hidden lg:flex items-center gap-3 bg-white border-2 border-yellow-500 rounded-full px-5 py-3 shadow-lg backdrop-blur-sm animate-fade-in">
        <MapPin className="w-5 h-5 text-yellow-600" />
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-zinc-500 leading-none mb-0.5">PROUDLY</span>
          <span className="text-sm font-bold text-black leading-none">100% Made in India</span>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-32 md:py-40">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-5 py-2.5 mb-8 backdrop-blur-sm animate-fade-in">
            <Sparkles className="w-4 h-4 text-yellow-600" />
            <span className="text-yellow-700 text-sm font-semibold tracking-wide">World-Class Welding Performance</span>
          </div>

          {/* Main Headline - Larger and Bolder */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-black mb-6 leading-[1.1] animate-fade-in-up">
            {heroContent.headline.split(' ').map((word, index) => (
              <span 
                key={index}
                className={word.toLowerCase() === 'welding' || word.toLowerCase() === 'industries' ? 'text-yellow-500' : ''}
              >
                {word}{' '}
              </span>
            ))}
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-zinc-600 mb-10 max-w-2xl leading-relaxed animate-fade-in-up">
            {heroContent.subheadline}
          </p>

          {/* Key Features - Refined */}
          <div className="flex flex-wrap gap-5 mb-12 text-zinc-600 text-sm font-medium animate-fade-in-up">
            <div className="flex items-center gap-2.5">
              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></div>
              <span>ISI Certified Equipment</span>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></div>
              <span>Trusted Since 2015</span>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></div>
              <span>Built for Professionals</span>
            </div>
          </div>

          {/* CTA Buttons - Refined */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up">
            <Button 
              size="lg"
              onClick={() => scrollToSection('products')}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-10 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group border-0"
            >
              View Products
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('dealer')}
              className="border-2 border-zinc-300 bg-white/80 text-zinc-800 hover:bg-white hover:border-yellow-500 px-10 py-6 text-base font-semibold backdrop-blur-sm transition-all duration-300"
            >
              Become a Dealer
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom Gradient - Softer */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white via-white/50 to-transparent z-0"></div>
    </section>
  );
};

export default HeroSection;